import { useState } from 'react';
import { toast } from 'sonner';
import { useUpdateBedarf } from '../../api/bedarfe';
import { Bedarf, ONBOARDING_STEPS, OnboardingStep } from '../../types/bedarf';

// Heroicons outline "information-circle" — matches the info icon used elsewhere in the app.
function InfoIcon({ title }: { title: string }) {
  return (
    <span className="ml-1.5 inline-flex text-gray-400 hover:text-gray-600 cursor-help" title={title}>
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="sr-only">{title}</span>
    </span>
  );
}

interface OnboardingChecklistProps {
  bedarf: Bedarf;
}

export function OnboardingChecklist({ bedarf }: OnboardingChecklistProps) {
  const updateMutation = useUpdateBedarf();
  const [pendingField, setPendingField] = useState<OnboardingStep['field'] | null>(null);

  // Editable in GEWONNEN too: a Bedarf may be won with steps still open, and
  // those have to be catchable afterwards. Unticking there moves nothing back.
  const doneCount = ONBOARDING_STEPS.filter((step) => bedarf[step.field]).length;
  const total = ONBOARDING_STEPS.length;

  const handleToggle = async (step: OnboardingStep, checked: boolean) => {
    setPendingField(step.field);
    try {
      await updateMutation.mutateAsync({
        id: bedarf.id,
        data: { [step.field]: checked },
      });
      toast.success(`„${step.label}" ${checked ? 'erledigt' : 'zurückgesetzt'}`);
    } catch (error) {
      console.error('Onboarding step update failed:', error);
      toast.error('Schritt konnte nicht gespeichert werden');
    } finally {
      setPendingField(null);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Onboarding
        </h3>
        <span className="text-sm font-medium text-gray-500">
          {doneCount} / {total} erledigt
        </span>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <ul className="space-y-3">
          {ONBOARDING_STEPS.map((step) => {
            const checked = bedarf[step.field];
            const isPending = pendingField === step.field;
            return (
              <li key={step.field} className="flex items-start">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isPending}
                    onChange={(e) => handleToggle(step, e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{step.label}</span>
                </label>
                <InfoIcon title={step.description} />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default OnboardingChecklist;
